import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'next-i18next';
import * as yup from 'yup';

import { ISettings } from '@/modules/settings/shared/types';
import { CircularBgImage, Typography } from '@/shared/components/atoms';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';

import ExleminationMark from '~/images/shared/ExleminationMark.svg';

export const useSettings = () => {
  const { t } = useTranslation();

  const getCropsTable = () => {
    const columnHelper = createColumnHelper<ISettings.ICropWithVarieties>();

    return [
      columnHelper.accessor('name', {
        header: t('crop'),
        cell: ({
          row: {
            original: { image, isDefault, name },
          },
        }) => (
          <div className='flex items-center gap-8'>
            {image && (
              <CircularBgImage
                bgColor='bg-blue-100'
                imageClassName='scale-125'
                imageSrc={image}
              />
            )}
            <Typography weight='medium'>{name}</Typography>
            {isDefault === 0 ? (
              <TippyTooltip
                arrow
                maxWidth={194}
                content={
                  <Typography variant='caption'>
                    {t('crop-custom-created')}
                  </Typography>
                }
              >
                <span className='text-primary-default'>
                  <ExleminationMark />
                </span>
              </TippyTooltip>
            ) : (
              ''
            )}
          </div>
        ),
      }),
      columnHelper.accessor('varieties', {
        header: t('Varieties'),
        cell: ({
          row: {
            original: { varieties },
          },
        }) => {
          return varieties && varieties.length ? (
            <PartialListWithTooltip
              limit={4}
              items={varieties.map((variety) => ({
                description: (
                  <div className=''>
                    <span>{variety.name}</span>
                    {variety.isDefault === 0 ? (
                      <span className='text-surface-10'> ({t('custom')})</span>
                    ) : (
                      ''
                    )}
                  </div>
                ),
              }))}
            />
          ) : (
            '-'
          );
        },
      }),
      // columnHelper.display({
      //   id: 'cropActions',
      //   cell: () => (
      //     <div className='flex items-center justify-end gap-24'>
      //       <span className='cursor-pointer text-primary-default'>
      //         <Edit width='24' height='24' />
      //       </span>
      //       <span className='cursor-pointer text-primary-default'>
      //         <HorizontalHollowDots width='24' height='24' />
      //       </span>
      //     </div>
      //   ),
      // }),
    ];
  };

  const cropSchema: yup.SchemaOf<ISettings.ICreateCropFormValues> = yup.object({
    cropId: yup
      .number()
      .typeError(t('this-field-is-required'))
      .required(t('this-field-is-required')),
    varieties: yup
      .array()
      .of(yup.number().nullable().notRequired())
      .nullable()
      .notRequired(),
  });

  const getCropVarieties = (
    data: ISettings.ICropWithVarieties[] | undefined,
    cropId: number | null
  ) => {
    return data?.find((crop) => crop.id === cropId)?.varieties;
  };

  return {
    cropSchema,
    getCropsTable,
    getCropVarieties,
  };
};
